// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Shared content area for bills browsers: cache status banners,
 * loading/error states, bill count, and data table.
 */
import type { ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { DataTable } from '@/components/ui/data-table'
import type { ColumnDef } from '@tanstack/react-table'

export interface BillsCacheContentProps<T> {
  cacheStatus: string | null
  loading: boolean
  error: string | null
  testIdPrefix?: string
  /** Total bills (unfiltered) */
  totalBills: number
  /** Filtered bills to display */
  filteredBills: T[]
  columns: ColumnDef<T, unknown>[]
  pageSize: number
  /** Message shown in the blue "refreshing" banner */
  refreshingMessage: string
  /** Label for the loading spinner (e.g. "Loading Maryland bills...") */
  loadingMessage: string
  /** Optional extra status shown next to bill count (e.g. "Loading scores...") */
  statusExtra?: ReactNode
}

export function BillsCacheContent<T>({
  cacheStatus,
  loading,
  error,
  testIdPrefix,
  totalBills,
  filteredBills,
  columns,
  pageSize,
  refreshingMessage,
  loadingMessage,
  statusExtra,
}: BillsCacheContentProps<T>) {
  return (
    <>
      {cacheStatus === 'refreshing' && totalBills === 0 && (
        <div
          className="flex items-center gap-2 rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-sm text-foreground dark:border-blue-500/30 dark:bg-blue-400/10 mb-4"
          data-testid={testIdPrefix ? `${testIdPrefix}-refreshing` : undefined}
        >
          <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />
          <span>{refreshingMessage}</span>
        </div>
      )}
      {cacheStatus === 'stale' && (
        <div
          className="rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-foreground dark:border-yellow-500/30 dark:bg-yellow-400/10 mb-4"
          data-testid={testIdPrefix ? `${testIdPrefix}-stale` : undefined}
        >
          Refreshing cache in the background — showing cached data.
        </div>
      )}
      {loading && totalBills === 0 && (
        <div
          className="flex items-center justify-center py-10"
          data-testid={testIdPrefix ? `${testIdPrefix}-loading` : undefined}
        >
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">{loadingMessage}</span>
        </div>
      )}
      {error && (
        <div
          className="text-center text-destructive py-10"
          data-testid={testIdPrefix ? `${testIdPrefix}-error` : undefined}
        >
          {error}
        </div>
      )}
      {!(loading && totalBills === 0) && !error && (
        <>
          <div
            className="flex items-center gap-3 text-sm text-muted-foreground mb-2"
            data-testid={testIdPrefix ? `${testIdPrefix}-count` : undefined}
          >
            <span>
              {filteredBills.length === totalBills
                ? `${totalBills} bills`
                : `${filteredBills.length} of ${totalBills} bills`}
            </span>
            {statusExtra}
          </div>
          <div data-testid={testIdPrefix ? `${testIdPrefix}-table` : undefined}>
            <DataTable
              columns={columns}
              data={filteredBills}
              enablePagination
              pageSize={pageSize}
              testIdPrefix={testIdPrefix}
            />
          </div>
        </>
      )}
    </>
  )
}
